import React from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slide,
  Grid,
  Typography,
  CircularProgress,
  LinearProgress,
  Box,
} from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import mammoth from 'mammoth';
import { getToken } from '../../../utils/common';
import logger from '../../../utils/logger';

// Try jsdelivr with legacy build path for version 5.x
// Legacy build should be more compatible with react-pdf
const pdfjsVersion = pdfjs.version || '5.4.296';
const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
pdfjs.GlobalWorkerOptions.workerSrc = workerUrl;

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

const CACHE_EXPIRATION_MS = 60 * 60 * 1000;

class LRU {
  constructor(limit = 10) {
    this.limit = limit;
    this.map = new Map();
  }

  get(key) {
    if (!this.map.has(key)) return null;
    const val = this.map.get(key);

    if (val?.ts && Date.now() - val.ts > CACHE_EXPIRATION_MS) {
      this.map.delete(key);
      return null;
    }

    this.map.delete(key);
    this.map.set(key, val);
    return val;
  }

  set(key, val) {
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      val.ts = Date.now();
    }
    if (this.map.has(key)) this.map.delete(key);
    this.map.set(key, val);
    while (this.map.size > this.limit) {
      const [oldKey] = this.map.entries().next().value;
      this.map.delete(oldKey);
    }
  }

  delete(key) {
    this.map.delete(key);
  }
}
const memoryCache = new LRU(10);

const DB_NAME = 'DialogPreviewCache';
const STORE = 'files';
function idbOpen() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}
async function idbGetRecord(key) {
  try {
    const db = await idbOpen();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const g = store.get(key);
      g.onsuccess = () => resolve(g.result || null);
      g.onerror = () => reject(g.error);
    });
  } catch {
    return null;
  }
}
async function idbPutRecord(key, record) {
  try {
    const db = await idbOpen();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const p = store.put(record, key);
      p.onsuccess = () => resolve();
      p.onerror = () => reject(p.error);
    });
  } catch {
    // Ignore errors during cleanup
  }
}
async function idbDeleteKey(key) {
  try {
    const db = await idbOpen();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readwrite');
      const store = tx.objectStore(STORE);
      const d = store.delete(key);
      d.onsuccess = () => resolve();
      d.onerror = () => reject(d.error);
    });
  } catch {
    // Ignore errors during cleanup
  }
}
async function idbTouch(key) {
  const rec = await idbGetRecord(key);
  if (rec?.blob) await idbPutRecord(key, { blob: rec.blob, ts: Date.now() });
}
async function idbEvictToLimit(limit = 10) {
  try {
    const db = await idbOpen();
    const entries = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, 'readonly');
      const store = tx.objectStore(STORE);
      const list = [];
      const cur = store.openCursor();
      cur.onsuccess = (e) => {
        const cursor = e.target.result;
        if (cursor) {
          const { key } = cursor;
          const val = cursor.value;
          const ts = val && typeof val.ts === 'number' ? val.ts : 0;
          list.push({ key, ts });
          cursor.continue();
        } else resolve(list);
      };
      cur.onerror = () => reject(cur.error);
    });
    if (entries.length <= limit) return;
    entries.sort((a, b) => a.ts - b.ts);
    const toRemove = entries.length - limit;
    const deletePromises = entries.slice(0, toRemove).map((entry) => idbDeleteKey(entry.key));
    await Promise.all(deletePromises);
  } catch {
    // Ignore errors during cleanup
  }
}
async function idbSetLRU(key, blob, limit = 10) {
  await idbPutRecord(key, { blob, ts: Date.now() });
  await idbEvictToLimit(limit);
}
async function idbGetLRU(key) {
  const rec = await idbGetRecord(key);
  if (rec?.blob) {
    if (rec?.ts && Date.now() - rec.ts > CACHE_EXPIRATION_MS) {
      await idbDeleteKey(key);
      return null;
    }
    await idbTouch(key);
    return rec.blob;
  }
  return null;
}

const isPdfName = (n = '') => /\.pdf$/i.test(n);
const isImageName = (n = '') => /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(n);
const isAudioName = (n = '') => /\.(mp3|wav|m4a|ogg)$/i.test(n);
const isVideoName = (n = '') => /\.(mp4|webm|ogg|mov)$/i.test(n);
const isExcel = (n = '') => /\.(xlsx?|csv)$/i.test(n);
const isWord = (n = '') => /\.(docx?)$/i.test(n);
const isPowerPoint = (n = '') => /\.(pptx?)$/i.test(n);

function fmtMs(ms) {
  if (ms == null) return '';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
function fmtSec(ms) {
  if (ms == null) return '';
  return `${(ms / 1000).toFixed(2)}s`;
}
function fmtBytes(bytes) {
  if (!Number.isFinite(bytes)) return '';
  if (bytes <= 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(sizes.length - 1, Math.floor(Math.log(bytes) / Math.log(k)));
  return `${Number.parseFloat((bytes / k ** i).toFixed(2))} ${sizes[i]}`;
}

function useInView(containerRef, rootMargin = '800px 0px') {
  const [inView, setInView] = React.useState(false);
  const ref = React.useRef(null);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rootEl = containerRef && 'current' in containerRef ? containerRef.current : null;
    const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), {
      root: rootEl || null,
      rootMargin,
      threshold: 0.01,
    });
    io.observe(el);
    return () => {
      io.disconnect();
    };
  }, [containerRef, rootMargin]);

  return { ref, inView };
}

function useThrottledNumber(source, { interval = 250, epsilon = 0 } = {}) {
  const [val, setVal] = React.useState(source || 0);
  const lastCommitRef = React.useRef(val);
  const lastTsRef = React.useRef(0);
  const rafIdRef = React.useRef(0);
  const srcRef = React.useRef(source);
  srcRef.current = source;

  React.useEffect(() => {
    const loop = () => {
      const now = performance.now();
      const src = srcRef.current ?? 0;
      const delta = Math.abs(src - lastCommitRef.current);
      if (now - lastTsRef.current >= interval && delta >= epsilon) {
        lastTsRef.current = now;
        lastCommitRef.current = src;
        setVal(src);
      }
      rafIdRef.current = requestAnimationFrame(loop);
    };
    rafIdRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [interval, epsilon]);

  return val;
}

function useThrottledNow(enabled, interval = 250) {
  const [now, setNow] = React.useState(() => performance.now());
  const lastTsRef = React.useRef(0);
  const rafIdRef = React.useRef(0);
  React.useEffect(() => {
    if (!enabled) return;
    const loop = () => {
      const t = performance.now();
      if (t - lastTsRef.current >= interval) {
        lastTsRef.current = t;
        setNow(t);
      }
      rafIdRef.current = requestAnimationFrame(loop);
    };
    rafIdRef.current = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafIdRef.current);
    };
  }, [enabled, interval]);
  return now;
}

/** Modern animated linear loader with optional percentage */
function ModernLinearLoader({ label = 'Loading...', percent = null }) {
  const showPercent = Number.isFinite(percent) && percent >= 0 && percent <= 100;

  return (
    <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto', textAlign: 'center' }}>
      <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
        {label} {showPercent ? `${Math.round(percent)}%` : ''}
      </Typography>

      <Box
        sx={(theme) => ({
          position: 'relative',
          '@keyframes subtleGlow': {
            '0%': { boxShadow: '0 0 0px rgba(33,101,209,0.25)' },
            '50%': { boxShadow: '0 0 18px rgba(33,101,209,0.35)' },
            '100%': { boxShadow: '0 0 0px rgba(33,101,209,0.25)' },
          },
          borderRadius: 999,
          p: 0.3,
          background:
            theme.palette.mode === 'dark'
              ? 'linear-gradient(90deg, rgba(255,255,255,0.08), rgba(255,255,255,0.12))'
              : 'linear-gradient(90deg, rgba(33,101,209,0.08), rgba(33,101,209,0.15))',
          animation: 'subtleGlow 2s ease-in-out infinite',
        })}
      >
        <LinearProgress
          variant={showPercent ? 'determinate' : 'indeterminate'}
          value={showPercent ? Math.max(0, Math.min(100, percent)) : undefined}
          sx={{
            height: 12,
            borderRadius: 999,
            backgroundColor: 'transparent',
            overflow: 'hidden',
            '& .MuiLinearProgress-bar': {
              borderRadius: 999,
              background: 'linear-gradient(90deg, rgba(33,101,209,1), rgba(33,101,209,0.8), rgba(33,101,209,1))',
              animationDuration: '0.8s',
              transition: 'width 200ms ease-out, transform 200ms ease-out',
            },
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 1,
          height: 6,
          maxWidth: 120,
          mx: 'auto',
          borderRadius: 999,
          opacity: 0.25,
          '@keyframes pulse': {
            '0%': { transform: 'scaleX(0.92)' },
            '50%': { transform: 'scaleX(1.0)' },
            '100%': { transform: 'scaleX(0.92)' },
          },
          background: 'currentColor',
          animation: 'pulse 1.6s ease-in-out infinite',
        }}
      />
    </Box>
  );
}

ModernLinearLoader.propTypes = {
  label: PropTypes.string,
  percent: PropTypes.number,
};

// Helper functions moved to outer scope for better performance
async function cacheAndStoreBlobOnly(url, token, key, signal) {
  try {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
    if (!res.ok) throw new Error(`Cache fetch failed: ${res.status}`);
    const blob = await res.blob();
    memoryCache.set(key, { blob, size: blob.size });
    await idbSetLRU(key, blob, 10);
  } catch {}
}

function triggerDownload(url, name) {
  const a = document.createElement('a');
  a.href = url;
  a.download = name || 'file';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export default function DialogPreview({ open, handleClose, path, filename, maxHeight = '70vh', onMetrics }) {
  const [phase, setPhase] = React.useState('idle');
  const [error, setError] = React.useState(null);
  // MODIFIED: Added state to trigger a refresh.
  const [refreshKey, setRefreshKey] = React.useState(0);

  // Object URL state (image/video/audio/office)
  const [objectUrl, setObjectUrl] = React.useState('');
  const objectUrlRef = React.useRef('');

  // PDF state
  const [pdfPages, setPdfPages] = React.useState(0);
  const [pdfProgressRaw, setPdfProgressRaw] = React.useState(0);
  const pdfProgress = useThrottledNumber(pdfProgressRaw, { interval: 150 });

  const pdfBlobUrlRef = React.useRef(null);

  const abortRef = React.useRef(null);
  const scrollRef = React.useRef(null);
  const [containerWidth, setContainerWidth] = React.useState(800);

  // Timing + progress bytes
  const startRef = React.useRef(0);
  const [docLoadMs, setDocLoadMs] = React.useState(null);
  const [firstViewMs, setFirstViewMs] = React.useState(null);

  // Raw progress refs -> throttled UI
  const bytesLoadedRef = React.useRef(0);
  const totalBytesRef = React.useRef(0);
  const [uiBytesLoaded, setUiBytesLoaded] = React.useState(0);
  const [uiTotalBytes, setUiTotalBytes] = React.useState(0);

  // Session-busted URL (for direct fetch with Authorization)
  const [sessionUrl, setSessionUrl] = React.useState('');
  const docxContainerRef = React.useRef(null);

  React.useEffect(() => {
    let rafId;
    const commitInterval = 150;
    let lastTs = 0;
    const loop = () => {
      const now = performance.now();
      if (now - lastTs >= commitInterval) {
        lastTs = now;
        setUiBytesLoaded(bytesLoadedRef.current);
        setUiTotalBytes(totalBytesRef.current);
      }
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, []);

  const nowTs = useThrottledNow(open && phase === 'loading', 150);
  const elapsedMs = phase === 'loading' ? Math.max(0, nowTs - startRef.current) : 0;

  React.useLayoutEffect(() => {
    function update() {
      const w = scrollRef.current?.clientWidth || 800;
      setContainerWidth(Math.min(980, w - 32));
    }
    update();
    const ro = new ResizeObserver(update);
    if (scrollRef.current) ro.observe(scrollRef.current);
    return () => {
      ro.disconnect();
    };
  }, []);

  const apiUrlBase = React.useMemo(() => {
    if (!path) return '';
    const encoded = encodeURIComponent(path.replaceAll('/', '%2F'));
    return `${globalThis.REACT_APP_API_PURE}record/download/file/${encoded}`;
  }, [path]);

  const cacheKey = React.useMemo(() => (path ? `file:${path}` : ''), [path]);
  const authToken = React.useMemo(() => getToken?.() || '', [open]);

  const pdfFile = React.useMemo(() => {
    if (!isPdfName(filename)) return null;
    if (pdfBlobUrlRef.current) return pdfBlobUrlRef.current;
    if (sessionUrl) {
      return { url: sessionUrl, httpHeaders: { Authorization: `Bearer ${authToken}` }, withCredentials: false };
    }
    return null;
  }, [sessionUrl, authToken, filename]);

  const pdfOptions = React.useMemo(
    () => ({
      standardFontDataUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/standard_fonts/',
      cMapUrl: 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.0.379/cmaps/',
      cMapPacked: true,
    }),
    []
  );

  const revokeCurrentUrls = React.useCallback(() => {
    if (objectUrlRef.current) {
      try {
        URL.revokeObjectURL(objectUrlRef.current);
      } catch {
        /* Ignore URL revocation errors */
      }
      objectUrlRef.current = '';
    }
    if (pdfBlobUrlRef.current) {
      try {
        URL.revokeObjectURL(pdfBlobUrlRef.current);
      } catch {
        /* Ignore URL revocation errors */
      }
      pdfBlobUrlRef.current = null;
    }
  }, []);

  // MODIFIED: Added `refreshKey` to the dependency array to allow re-triggering this effect.
  React.useEffect(() => {
    if (!open) return;

    const array = new Uint32Array(2);
    crypto.getRandomValues(array);
    const bust = Date.now().toString(36) + Array.from(array, (n) => n.toString(36)).join('');

    const sep = apiUrlBase.includes('?') ? '&' : '?';
    const sessUrl = apiUrlBase ? `${apiUrlBase}${sep}v=${bust}` : '';
    setSessionUrl(sessUrl);

    setPhase('loading');
    setError(null);
    setPdfPages(0);
    setPdfProgressRaw(0);
    setDocLoadMs(null);
    setFirstViewMs(null);
    startRef.current = performance.now();

    bytesLoadedRef.current = 0;
    totalBytesRef.current = 0;
    setUiBytesLoaded(0);
    setUiTotalBytes(0);

    revokeCurrentUrls();

    abortRef.current?.abort?.();
    abortRef.current = new AbortController();

    (async () => {
      try {
        if (cacheKey) {
          const mem = memoryCache.get(cacheKey);
          if (mem && mem.blob instanceof Blob) {
            const size = mem.size ?? mem.blob.size ?? 0;
            totalBytesRef.current = size;
            bytesLoadedRef.current = size;
            setUiTotalBytes(size);
            setUiBytesLoaded(size);

            const freshUrl = URL.createObjectURL(mem.blob);
            if (isPdfName(filename)) {
              pdfBlobUrlRef.current = freshUrl;
            } else {
              objectUrlRef.current = freshUrl;
              setObjectUrl(freshUrl);
            }
            return;
          }
        }

        let idbBlob = null;
        if (cacheKey) idbBlob = await idbGetLRU(cacheKey);
        if (idbBlob instanceof Blob) {
          memoryCache.set(cacheKey, { blob: idbBlob, size: idbBlob.size });

          const size = idbBlob.size || 0;
          totalBytesRef.current = size;
          bytesLoadedRef.current = size;
          setUiTotalBytes(size);
          setUiBytesLoaded(size);

          const freshUrl = URL.createObjectURL(idbBlob);
          if (isPdfName(filename)) {
            pdfBlobUrlRef.current = freshUrl;
          } else {
            objectUrlRef.current = freshUrl;
            setObjectUrl(freshUrl);
          }
          return;
        }

        if (isPdfName(filename)) {
          if (sessUrl) cacheAndStoreBlobOnly(sessUrl, authToken, cacheKey, abortRef.current.signal).catch(() => {});
          return;
        }

        if (sessUrl) {
          await fetchStreamToBlobAndCache({
            url: sessUrl,
            token: authToken,
            signal: abortRef.current.signal,
            onChunk: ({ loaded, total }) => {
              bytesLoadedRef.current = loaded;
              if (total) totalBytesRef.current = total;
            },
            onDone: (blob) => {
              totalBytesRef.current = blob.size;
              bytesLoadedRef.current = blob.size;
              setUiTotalBytes(blob.size);
              setUiBytesLoaded(blob.size);

              const freshUrl = URL.createObjectURL(blob);
              objectUrlRef.current = freshUrl;
              setObjectUrl(freshUrl);

              memoryCache.set(cacheKey, { blob, size: blob.size });
              idbSetLRU(cacheKey, blob, 10).catch(() => {});
            },
          });
        }
      } catch (e) {
        if (e.name !== 'AbortError') {
          setError(e);
          setPhase('error');
        }
      }
    })();

    return () => {
      abortRef.current?.abort?.();
      revokeCurrentUrls();
    };
  }, [open, apiUrlBase, filename, cacheKey, authToken, revokeCurrentUrls, refreshKey]);

  function fetchStreamToBlobAndCache({ url, token, signal, onChunk, onDone }) {
    return (async () => {
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
      if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);

      const totalHeader = Number(res.headers.get('content-length')) || 0;
      if (totalHeader > 0) totalBytesRef.current = totalHeader;

      if (!res.body?.getReader) {
        const blob = await res.blob();
        onChunk?.({ loaded: blob.size || totalHeader, total: blob.size || totalHeader });
        onDone?.(blob);
        return;
      }

      const reader = res.body.getReader();
      const chunks = [];
      let loaded = 0;
      let done = false;

      while (!done) {
        const { value, done: d } = await reader.read();
        done = d;
        if (value) {
          chunks.push(value);
          loaded += value.byteLength;
          onChunk?.({ loaded, total: totalHeader || undefined });
        }
      }

      const blob = new Blob(chunks);
      onDone?.(blob);
    })();
  }

  const handleDownload = React.useCallback(async () => {
    try {
      if (cacheKey) {
        const mem = memoryCache.get(cacheKey);
        if (mem?.blob instanceof Blob) {
          const tempUrl = URL.createObjectURL(mem.blob);
          triggerDownload(tempUrl, filename);
          URL.revokeObjectURL(tempUrl);
          return;
        }
        const idbBlob = await idbGetLRU(cacheKey);
        if (idbBlob instanceof Blob) {
          const tempUrl = URL.createObjectURL(idbBlob);
          triggerDownload(tempUrl, filename);
          URL.revokeObjectURL(tempUrl);
          memoryCache.set(cacheKey, { blob: idbBlob, size: idbBlob.size });
          return;
        }
      }

      if (sessionUrl) {
        const res = await fetch(sessionUrl, { headers: { Authorization: `Bearer ${authToken}` } });
        if (!res.ok) throw new Error('Download failed');
        const blob = await res.blob();
        const tempUrl = URL.createObjectURL(blob);
        triggerDownload(tempUrl, filename);
        URL.revokeObjectURL(tempUrl);
        if (cacheKey) {
          memoryCache.set(cacheKey, { blob, size: blob.size });
          idbSetLRU(cacheKey, blob, 10).catch(() => {});
        }
      }
    } catch (e) {
      logger.error('Error in preview:', e);
    }
  }, [sessionUrl, authToken, filename, cacheKey]);

  // MODIFIED: Added a handler to clear cache and trigger a refresh.
  const handleRefresh = React.useCallback(async () => {
    if (cacheKey) {
      memoryCache.delete(cacheKey);
      await idbDeleteKey(cacheKey);
    }
    setRefreshKey((k) => k + 1);
  }, [cacheKey]);

  const markFirstView = React.useCallback(() => {
    if (firstViewMs !== null) return;
    const ms = performance.now() - startRef.current;
    setFirstViewMs(ms);
    setPhase('ready');
    onMetrics?.({ firstViewMs: ms, docLoadMs });
  }, [firstViewMs, docLoadMs, onMetrics]);

  const genericPercent = React.useMemo(() => {
    if (uiTotalBytes > 0) return Math.min(100, Math.round((uiBytesLoaded / uiTotalBytes) * 100));
    return null;
  }, [uiBytesLoaded, uiTotalBytes]);

  const renderPdf = () => (
    <div
      ref={scrollRef}
      style={{ minHeight: '320px', maxHeight, overflowY: 'auto', padding: 8, background: 'transparent' }}
    >
      <Document
        key={pdfBlobUrlRef.current ? pdfBlobUrlRef.current : sessionUrl}
        file={pdfFile}
        loading={
          <Box
            sx={{
              minHeight: 320,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
              width: '100%',
            }}
          >
            <ModernLinearLoader label="Loading..." percent={Math.round(pdfProgress * 100)} />
          </Box>
        }
        onLoadProgress={({ loaded, total }) => {
          bytesLoadedRef.current = loaded || 0;
          if (total) totalBytesRef.current = total;
          if (total) setPdfProgressRaw(Math.max(0, Math.min(1, loaded / total)));
          if (!total && cacheKey) {
            const mem = memoryCache.get(cacheKey);
            if (mem?.blob?.size) {
              totalBytesRef.current = mem.blob.size;
              bytesLoadedRef.current = mem.blob.size;
            }
          }
        }}
        onLoadSuccess={(pdf) => {
          setPdfPages(pdf.numPages || 0);
          const ms = performance.now() - startRef.current;
          setDocLoadMs(ms);

          setTimeout(() => {
            if (firstViewMs === null) {
              const ms2 = performance.now() - startRef.current;
              setFirstViewMs(ms2);
              setPhase('ready');
              onMetrics?.({ firstViewMs: ms2, docLoadMs: ms });
            }
          }, 800);
        }}
        onLoadError={(err) => {
          logger.error('PDF load error:', err);
          setError(err);
          setPhase('error');
        }}
        options={pdfOptions}
      >
        {Array.from({ length: pdfPages }, (_, i) => (
          <LazyPdfPage
            key={i + 1}
            pageNumber={i + 1}
            containerRef={scrollRef}
            width={containerWidth}
            onFirstRendered={i === 0 ? markFirstView : undefined}
          />
        ))}
      </Document>
    </div>
  );

  const renderImage = () => (
    <div style={{ minHeight: '320px', maxHeight, overflow: 'auto', textAlign: 'center' }}>
      {objectUrl ? (
        <img
          src={objectUrl}
          alt={filename || 'preview'}
          style={{ maxWidth: '100%', height: 'auto' }}
          onLoad={markFirstView}
          onError={async () => {
            if (cacheKey) {
              const mem = memoryCache.get(cacheKey);
              if (mem?.blob instanceof Blob) {
                const fresh = URL.createObjectURL(mem.blob);
                if (objectUrlRef.current && objectUrlRef.current !== fresh) {
                  try {
                    URL.revokeObjectURL(objectUrlRef.current);
                  } catch {
                    /* Ignore URL revocation errors */
                  }
                }
                objectUrlRef.current = fresh;
                setObjectUrl(fresh);
                return;
              }
              const idbBlob = await idbGetLRU(cacheKey);
              if (idbBlob instanceof Blob) {
                memoryCache.set(cacheKey, { blob: idbBlob, size: idbBlob.size });
                const fresh = URL.createObjectURL(idbBlob);
                if (objectUrlRef.current && objectUrlRef.current !== fresh) {
                  try {
                    URL.revokeObjectURL(objectUrlRef.current);
                  } catch {
                    /* Ignore URL revocation errors */
                  }
                }
                objectUrlRef.current = fresh;
                setObjectUrl(fresh);
              }
            }
          }}
        />
      ) : (
        <ModernLinearLoader label="Loading..." percent={genericPercent} />
      )}
    </div>
  );

  const renderVideo = () => (
    <div style={{ minHeight: '320px', maxHeight, overflow: 'auto', textAlign: 'center' }}>
      {!objectUrl ? (
        <ModernLinearLoader label="Loading..." percent={genericPercent} />
      ) : (
        <video controls style={{ maxWidth: '100%' }} onLoadedData={markFirstView}>
          <source src={objectUrl} />
          <track kind="captions" src="" label="No captions available" />
        </video>
      )}
    </div>
  );

  const renderAudio = () => (
    <div style={{ minHeight: '320px', maxHeight, overflow: 'auto', textAlign: 'center' }}>
      {!objectUrl ? (
        <ModernLinearLoader label="Loading..." percent={genericPercent} />
      ) : (
        <audio controls style={{ width: '100%' }} onLoadedData={markFirstView}>
          <source src={objectUrl} />
          <track kind="captions" src="" label="No captions available" />
        </audio>
      )}
    </div>
  );

  // Office Online viewer URL for PowerPoint files
  const officeViewerUrl = React.useMemo(() => {
    // Office Online viewer requires a publicly accessible URL
    // For authenticated URLs, we'll try Office Online viewer, but it may not work
    if (sessionUrl) {
      try {
        const encodedUrl = encodeURIComponent(sessionUrl);
        return `https://view.officeapps.live.com/op/embed.aspx?src=${encodedUrl}`;
      } catch (e) {
        logger.warn('Failed to create Office Online viewer URL:', e);
        return null;
      }
    }
    return null;
  }, [sessionUrl]);

  const renderPowerPoint = () => {
    if (!objectUrl && !sessionUrl) {
      return (
        <Grid container alignItems="center" justifyContent="center" sx={{ p: 2, minHeight: 320 }}>
          <ModernLinearLoader label="Loading..." percent={genericPercent} />
        </Grid>
      );
    }

    // Try Office Online viewer first (requires public URL, may not work with authenticated URLs)
    if (officeViewerUrl) {
      return (
        <iframe
          title="presentation"
          src={officeViewerUrl}
          style={{ width: '100%', height: '100%', border: 0, minHeight: '600px' }}
          onLoad={markFirstView}
          onError={() => {
            logger.warn('Office Online viewer failed to load');
          }}
        />
      );
    }

    // Fallback: show message that preview requires download
    // Office Online viewer doesn't work with blob URLs or authenticated URLs
    return (
      <Grid container alignItems="center" justifyContent="center" sx={{ p: 4, minHeight: 320 }}>
        <Box sx={{ textAlign: 'center', maxWidth: 480 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            PowerPoint Preview
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Preview is not available for this file. Please download it to view the presentation using Microsoft
            PowerPoint or another compatible application.
          </Typography>
          <Button
            onClick={handleDownload}
            variant="contained"
            sx={{
              backgroundColor: '#2165d1',
              color: 'white',
              '&:hover': { backgroundColor: '#1b55b7' },
            }}
          >
            Download File
          </Button>
        </Box>
      </Grid>
    );
  };

  const renderOffice = () => (
    <div style={{ minHeight: '320px', height: maxHeight, width: '100%' }}>
      {!objectUrl ? (
        <ModernLinearLoader label="Loading..." percent={genericPercent} />
      ) : isExcel(filename) ? (
        <iframe
          title="sheet"
          src={objectUrl}
          style={{ width: '100%', height: '100%', border: 0 }}
          onLoad={markFirstView}
        />
      ) : isPowerPoint(filename) ? (
        renderPowerPoint()
      ) : (
        <>
          <style>
            {`
              .docx-container {
                width: 100%;
                height: 100%;
                overflow: auto;
                padding: 20px;
                background-color: #fff;
                font-family: 'Times New Roman', serif;
                line-height: 1.6;
              }
              .docx-container p {
                margin: 0.5em 0;
              }
              .docx-container table {
                border-collapse: collapse;
                width: 100%;
                margin: 1em 0;
              }
              .docx-container table td,
              .docx-container table th {
                border: 1px solid #ddd;
                padding: 8px;
              }
              .docx-container h1,
              .docx-container h2,
              .docx-container h3,
              .docx-container h4,
              .docx-container h5,
              .docx-container h6 {
                margin: 1em 0 0.5em 0;
              }
              .docx-container ul,
              .docx-container ol {
                margin: 0.5em 0;
                padding-left: 2em;
              }
            `}
          </style>
          <div
            ref={(el) => {
              docxContainerRef.current = el;
            }}
            className="docx-container"
            style={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              padding: 20,
              backgroundColor: '#fff',
              fontFamily: "'Times New Roman', serif",
              lineHeight: 1.6,
            }}
          />
        </>
      )}
    </div>
  );

  const body = React.useMemo(() => {
    if (!open) return null;
    if (!path || !filename) return <Typography sx={{ p: 2 }}>No file selected.</Typography>;
    if (isPdfName(filename)) {
      if (!pdfFile) {
        return (
          <Grid container alignItems="center" justifyContent="center" sx={{ p: 2, minHeight: 320 }}>
            <ModernLinearLoader label="Loading..." percent={genericPercent} />
          </Grid>
        );
      }
      return renderPdf();
    }
    if (isImageName(filename)) return renderImage();
    if (isVideoName(filename)) return renderVideo();
    if (isAudioName(filename)) return renderAudio();
    if (isExcel(filename) || isWord(filename) || isPowerPoint(filename)) return renderOffice();
    return (
      <Grid container alignItems="center" justifyContent="center" sx={{ p: 2, minHeight: 320 }}>
        {phase === 'error' ? <Typography color="error">Unsupported file type</Typography> : <ModernLinearLoader />}
      </Grid>
    );
  }, [open, path, filename, phase, pdfPages, objectUrl, containerWidth, sessionUrl, pdfFile, genericPercent]);

  // Render DOCX using mammoth when blob/object is ready
  React.useEffect(() => {
    if (!open || !isWord(filename)) return;

    // Wait for either objectUrl (cached blob) or sessionUrl (API URL) to be ready
    if (!objectUrl && !sessionUrl) return;

    let mounted = true;

    const renderDocxContent = async () => {
      // Wait for container to be mounted with retry logic
      let retries = 0;
      const maxRetries = 20; // 20 * 50ms = 1 second max wait

      while (retries < maxRetries && mounted) {
        const container = docxContainerRef.current;
        if (container && container instanceof HTMLElement) {
          break;
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
        retries++;
      }

      if (!mounted) return;

      const container = docxContainerRef.current;
      if (!container || !(container instanceof HTMLElement)) {
        logger.error('DOCX container not found or invalid after retries');
        setPhase('error');
        setError(new Error('Container element not available'));
        return;
      }

      // Try to get the blob from memory cache first
      let blob = null;
      if (cacheKey) {
        const mem = memoryCache.get(cacheKey);
        if (mem?.blob instanceof Blob) blob = mem.blob;
        if (!blob) {
          const idbBlob = await idbGetLRU(cacheKey);
          if (idbBlob instanceof Blob && mounted) blob = idbBlob;
        }
      }
      // Fallback to fetching from sessionUrl (API URL with auth) or objectUrl
      if (!blob && mounted) {
        try {
          // Prefer sessionUrl if available (actual API URL with auth)
          const urlToFetch = sessionUrl || objectUrlRef.current;
          if (urlToFetch && !urlToFetch.startsWith('blob:')) {
            const res = await fetch(urlToFetch, {
              headers: { Authorization: `Bearer ${authToken}` },
            });
            if (res.ok) {
              blob = await res.blob();
              // Cache the blob for future use
              if (cacheKey && blob) {
                memoryCache.set(cacheKey, { blob, size: blob.size });
                idbSetLRU(cacheKey, blob, 10).catch(() => {});
              }
            } else {
              throw new Error(`Failed to fetch DOCX: ${res.status} ${res.statusText}`);
            }
          } else if (objectUrlRef.current && objectUrlRef.current.startsWith('blob:')) {
            // If it's a blob URL, fetch it directly
            const res = await fetch(objectUrlRef.current);
            if (res.ok) blob = await res.blob();
          }
        } catch (e) {
          logger.error('Failed to fetch blob for DOCX:', e);
        }
      }
      if (!blob || !mounted) return;

      // Clear previous render - remove all child nodes
      try {
        if (mounted && container) {
          while (container.firstChild) {
            container.removeChild(container.firstChild);
          }
        }
      } catch (e) {
        logger.error('Failed to clear container:', e);
      }
      if (!mounted) return;

      // Render DOCX using mammoth (converts DOCX to HTML)
      try {
        if (!container || !(container instanceof HTMLElement)) {
          throw new Error('Container is not a valid DOM element');
        }

        if (!blob || !(blob instanceof Blob)) {
          throw new Error('Blob is not valid');
        }

        // Use requestAnimationFrame to ensure DOM is ready
        await new Promise((resolve) => requestAnimationFrame(resolve));
        if (!mounted) return;

        // Check blob type
        if (
          !blob.type.includes('word') &&
          !blob.type.includes('docx') &&
          blob.type !== 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        ) {
          logger.warn('Blob type:', blob.type, 'Expected DOCX type - will attempt conversion anyway');
        }

        // Blob created successfully

        // Convert DOCX blob to HTML using mammoth
        let arrayBuffer;
        try {
          arrayBuffer = await blob.arrayBuffer();
          if (!arrayBuffer || arrayBuffer.byteLength === 0) {
            throw new Error('Blob arrayBuffer is empty or invalid');
          }
        } catch (arrayBufferError) {
          logger.error('Failed to convert blob to arrayBuffer:', arrayBufferError);
          throw new Error('Failed to read DOCX file. The file may be corrupted or in an unsupported format.');
        }

        if (!mounted) return;

        let result;
        try {
          result = await mammoth.convertToHtml({ arrayBuffer });
        } catch (mammothError) {
          logger.error('Mammoth conversion error:', mammothError);
          throw new Error(`Failed to convert DOCX: ${mammothError.message || 'Unknown error'}`);
        }

        if (!mounted) return;

        // Set the HTML content
        if (result && result.value) {
          container.innerHTML = result.value;
        } else {
          throw new Error('Mammoth conversion returned empty result');
        }

        // Handle any warnings
        if (result.messages && result.messages.length > 0) {
          logger.warn('Mammoth conversion warnings:', result.messages);
        }

        if (mounted) {
          markFirstView();
        }
      } catch (e) {
        // Fall back to error phase if render fails
        if (!mounted) return;
        logger.error('DOCX render error:', e);
        setPhase('error');
        setError(
          new Error(
            e.message ||
              'Failed to render DOCX file. Please download and open it using Microsoft Word or another compatible application.'
          )
        );
      }
    };

    renderDocxContent();

    return () => {
      mounted = false;
    };
  }, [open, filename, objectUrl, sessionUrl, cacheKey, authToken, markFirstView]);

  const speedBytesPerSec = elapsedMs > 0 ? uiBytesLoaded / (elapsedMs / 1000) : 0;
  const remainingMs =
    speedBytesPerSec > 0 && uiTotalBytes > 0 ? ((uiTotalBytes - uiBytesLoaded) / speedBytesPerSec) * 1000 : null;

  const timingLabel =
    firstViewMs !== null
      ? `First view: ${fmtMs(firstViewMs)}${docLoadMs === null ? '' : ` (Doc: ${fmtMs(docLoadMs)})`}`
      : phase === 'loading'
      ? `Loading… ${fmtBytes(uiBytesLoaded)}${uiTotalBytes ? ` / ${fmtBytes(uiTotalBytes)}` : ''} • ${fmtSec(
          elapsedMs
        )} elapsed${remainingMs != null ? ` • ~${fmtSec(remainingMs)} left` : ''}`
      : '';

  const Footer = (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr minmax(240px, 320px) auto',
          sm: '1fr minmax(260px, 340px) auto',
        },
        alignItems: 'center',
        gap: 2,
        width: '100%',
      }}
    >
      <Typography
        variant="body2"
        sx={{
          opacity: 0.9,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'normal',
          overflow: 'visible',
          wordBreak: 'break-word',
          lineHeight: 1.2,
        }}
      >
        ⏱️ {timingLabel}
      </Typography>

      <Box sx={{ minWidth: { xs: 240, sm: 260 }, maxWidth: 340 }} />

      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1 }}>
        <Button
          onClick={handleClose}
          style={{
            minWidth: 104,
            backgroundColor: '#2165d1',
            color: 'white',
            '&:hover': { backgroundColor: '#1b55b7' },
          }}
          sx={{ minWidth: 104, backgroundColor: '#2165d1', color: 'white', '&:hover': { backgroundColor: '#1b55b7' } }}
        >
          Close
        </Button>
        <Button
          onClick={handleDownload}
          disabled={phase === 'loading'}
          sx={{
            minWidth: 140,
            backgroundColor: phase === 'loading' ? '#a6b9e6' : '#2165d1',
            color: 'white',
            '&:hover': { backgroundColor: phase === 'loading' ? '#a6b9e6' : '#1b55b7' },
          }}
          style={{
            minWidth: 140,
            backgroundColor: phase === 'loading' ? '#a6b9e6' : '#2165d1',
            color: 'white',
            '&:hover': { backgroundColor: phase === 'loading' ? '#a6b9e6' : '#1b55b7' },
          }}
        >
          Download File
        </Button>
      </Box>
    </Box>
  );

  return (
    <Dialog
      open={open}
      TransitionComponent={Transition}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-describedby="preview-dialog"
      keepMounted
    >
      <DialogTitle sx={{ pr: 2 }} title={filename || 'Preview'}>
        {filename || 'Preview'}
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* MODIFIED: Replaced the error display with a version that includes the refresh button. */}
        {phase === 'error' ? (
          <Box
            sx={{
              p: 2,
              textAlign: 'center',
              minHeight: 320,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography color="error" sx={{ mb: 2 }}>
              Failed to load file.
              {process.env.NODE_ENV === 'development' && error ? ` (${String(error)})` : ''}
            </Typography>
            <Button onClick={handleRefresh} style={{ backgroundColor: '#2165d1' }}>
              Try Again
            </Button>
          </Box>
        ) : (
          body
        )}
      </DialogContent>

      <DialogActions sx={{ alignItems: 'center' }}>{Footer}</DialogActions>
    </Dialog>
  );
}

DialogPreview.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  path: PropTypes.string,
  filename: PropTypes.string,
  maxHeight: PropTypes.string,
  onMetrics: PropTypes.func,
};

function LazyPdfPage({ pageNumber, containerRef, width, onFirstRendered }) {
  const { ref, inView } = useInView(containerRef, '1000px 0px');

  return (
    <div ref={ref} style={{ margin: '12px 0' }}>
      {inView ? (
        <Page
          pageNumber={pageNumber}
          width={width}
          renderAnnotationLayer={false}
          renderTextLayer={false}
          onRenderSuccess={() => {
            if (onFirstRendered) onFirstRendered();
          }}
        />
      ) : (
        <div style={{ height: 900, background: 'transparent' }}>
          <Grid container alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
            <CircularProgress size={18} />
            <Typography sx={{ ml: 1 }} variant="caption">
              Preparing page {pageNumber}…
            </Typography>
          </Grid>
        </div>
      )}
    </div>
  );
}

LazyPdfPage.propTypes = {
  pageNumber: PropTypes.number,
  containerRef: PropTypes.object,
  width: PropTypes.number,
  onFirstRendered: PropTypes.func,
};
